import React from 'react'
import { Box, Stack } from '@mui/material'
import { useTranslation } from 'react-i18next'

import { availableToColor } from '../../../../helpers'
import EquipmentStatusChip from '../../EquipmentStatusChip'
import { DetailTable } from '../components/DetailTable'
import { FractionValue, MetricWidget } from '../components/MetricWidget'
import { DetailCardGrid, InfoRow, Widget } from '../components/Widget'
import { formatBytes, formatPercentValue, getFirewallNode, getFirstMetricValue } from '../utils'

const FirewallVpnWidget = ({ equipment }) => {
  const { t } = useTranslation()
  const tunnels = equipment.tunnels_stats || []

  return (
    <Widget icon="fa-light fa-tornado" title={t('supervision.hardware.firewall.vpn')} sx={{ minHeight: 220 }}>
      <DetailTable
        columns={[
          { key: 'status', label: t('supervision.tables.status_col'), width: '12%', align: 'left', render: row => <EquipmentStatusChip equipment={{ available_level: row.up_status ? 'up' : 'down' }} compact /> },
          { key: 'phase1', label: t('supervision.hardware.firewall.phase1'), width: '14%' },
          { key: 'phase2', label: t('supervision.hardware.firewall.phase2'), width: '14%' },
          { key: 'remote_gateway_ip', label: t('supervision.hardware.firewall.remote_gateway'), width: '18%' },
          { key: 'local_gateway_ip', label: t('supervision.hardware.firewall.local_gateway'), width: '18%' },
          { key: 'rx_bytes', label: t('supervision.hardware.firewall.rx_bytes'), width: '12%' },
          { key: 'tx_bytes', label: t('supervision.hardware.firewall.tx_bytes'), width: '12%' },
        ]}
        rows={tunnels.map((tunnel, index) => ({
          id: index,
          phase1: tunnel.phase1_name,
          phase2: tunnel.phase2_name,
          remote_gateway_ip: tunnel.remote_gateway_ip,
          local_gateway_ip: tunnel.local_gateway_ip,
          rx_bytes: formatBytes(tunnel.rx_bytes),
          tx_bytes: formatBytes(tunnel.tx_bytes),
          up_status: tunnel.up_status
        }))}
      />
    </Widget>
  )
}

const FirewallNodeWidget = ({ title, node }) => {
  const { t } = useTranslation()
  const cpu = node?.cpu_usage
  const memory = node?.memory_usage

  return (
    <Widget title={title} sx={{ flex: 1, minHeight: 260 }}>
      <DetailCardGrid sx={{ gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, minmax(0, 1fr))' }, mb: 2 }}>
        <MetricWidget title={t('supervision.hardware.ap.cpu')} value={formatPercentValue(cpu)} unit="%" icon="fa-solid fa-microchip" progress={cpu == null ? null : Number(cpu)} showProgress />
        <MetricWidget title={t('supervision.hardware.firewall.memory')} value={formatPercentValue(memory)} unit="%" icon="fa-solid fa-memory" progress={memory == null ? null : Number(memory)} showProgress />
        <MetricWidget title={t('supervision.hardware.firewall.network')} value={node?.network_usage == null ? null : formatBytes(node.network_usage)} icon="fa-light fa-network-wired" />
      </DetailCardGrid>
      {[
        [t('supervision.hardware.firewall.synchronized'), node?.sync_status == null ? '-' : t(node.sync_status ? 'ui_elements.yes' : 'ui_elements.no')],
        [t('supervision.hardware.firewall.hostname'), node?.hostname],
        [t('supervision.popup.serial'), node?.serial_number],
        [t('supervision.hardware.firewall.sessions'), node?.session_count],
        [t('supervision.hardware.firewall.sessions_consumption'), node?.byte_count == null ? null : formatBytes(node.byte_count)]
      ].map(([label, value]) => (
        <InfoRow key={label} label={label} value={value} />
      ))}
    </Widget>
  )
}

const FirewallSections = ({ equipment }) => {
  const { t } = useTranslation()
  const tunnelsProgress = equipment.tunnels_up != null && equipment.total_tunnels ? (equipment.tunnels_up / equipment.total_tunnels) * 100 : null
  const autoSync = equipment.cluster_stats?.auto_sync ?? getFirstMetricValue(equipment, ['auto_sync', 'firewall_auto_sync'])
  const isAutoSyncActive = autoSync === true || String(autoSync).toLowerCase() === 'true'
  const masterNode = getFirewallNode(equipment, 'master', 0)
  const backupNode = getFirewallNode(equipment, 'backup', 1)

  return (
    <>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))', xl: 'repeat(4, minmax(0, 1fr))' }, gap: 2 }}>
        <MetricWidget
          title={t('supervision.hardware.firewall.active_vpn_tunnels')}
          icon="fa-solid fa-tornado"
          progress={tunnelsProgress}
          progressColor="#92BC62"
          progressTrackColor="#F7F7F7"
          progressRemainderColor="#DC2626"
          showProgress
        >
          <FractionValue current={equipment.tunnels_up} total={equipment.total_tunnels} currentColor="#92BC62" />
        </MetricWidget>
        <MetricWidget title={t('supervision.hardware.common.firmware')} value={equipment.firmware_version} icon="fa-solid fa-gear-code" />
        <MetricWidget title={t('supervision.hardware.firewall.cluster_group')} value={equipment.cluster_stats?.group_name || equipment.system_name || equipment.label} icon="fa-solid fa-hexagon-nodes" />
        <MetricWidget
          title={t('supervision.hardware.firewall.auto_sync')}
          value={autoSync == null ? null : t(isAutoSyncActive ? 'supervision.hardware.ap.active' : 'supervision.hardware.ap.inactive')}
          icon="fa-solid fa-arrows-spin"
          iconColor={autoSync == null ? '#3D3D3D' : availableToColor(isAutoSyncActive ? 'up' : 'down')}
        />
      </Box>
      <FirewallVpnWidget equipment={equipment} />
      <Stack direction={{ xs: 'column', lg: 'row' }} gap={2}>
        <FirewallNodeWidget title={t('supervision.hardware.firewall.master_node')} node={masterNode} />
        <FirewallNodeWidget title={t('supervision.hardware.firewall.backup_node')} node={backupNode} />
      </Stack>
    </>
  )
}

export default FirewallSections
